import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isInsufficientBalanceError, subscribeApiErrors } from '@fetmode/api-client';
import { useAuth, useAuthModal } from '@fetmode/app-core';

import { CoinShopOverlay } from './CoinShopOverlay.js';

/** Continuation to run after a successful top-up covers `neededCoins`. */
export type PendingSpendAction = () => void | Promise<void>;

export interface CoinShopContextValue {
  openCoinShop: (neededCoins?: number, onReady?: PendingSpendAction) => void;
  closeCoinShop: () => void;
  /**
   * Returns false and opens the shop when `balance` cannot cover `needed`.
   * Pass `onReady` to auto-retry the spend action after a successful purchase.
   */
  requireCoins: (balance: number, needed: number, onReady?: PendingSpendAction) => boolean;
  /** Fetches the spend wallet, then opens the shop when it cannot cover `needed`. */
  requireSpendCoins: (needed: number, onReady?: PendingSpendAction) => Promise<boolean>;
  lastWalletTotal: number | null;
}

const CoinShopContext = createContext<CoinShopContextValue | null>(null);

export interface CoinShopProviderProps {
  children: ReactNode;
}

export function CoinShopProvider({ children }: CoinShopProviderProps): ReactElement {
  const { api, user } = useAuth();
  const { requireAuth } = useAuthModal();
  const [isOpen, setIsOpen] = useState(false);
  const [neededCoins, setNeededCoins] = useState<number | null>(null);
  const [coinBalance, setCoinBalance] = useState(0);
  const [lastWalletTotal, setLastWalletTotal] = useState<number | null>(null);
  const pendingActionRef = useRef<PendingSpendAction | null>(null);
  const neededCoinsRef = useRef<number | null>(null);

  const clearPending = useCallback(() => {
    pendingActionRef.current = null;
    neededCoinsRef.current = null;
  }, []);

  const closeCoinShop = useCallback(() => {
    setIsOpen(false);
    setNeededCoins(null);
    clearPending();
  }, [clearPending]);

  const openCoinShop = useCallback(
    (needed?: number, onReady?: PendingSpendAction) => {
      requireAuth(() => {
        const nextNeeded =
          typeof needed === 'number' && Number.isFinite(needed) ? needed : null;
        neededCoinsRef.current = nextNeeded;
        pendingActionRef.current = onReady ?? null;
        setNeededCoins(nextNeeded);
        setIsOpen(true);
      }, 'wallet');
    },
    [requireAuth],
  );

  const requireCoins = useCallback(
    (balance: number, needed: number, onReady?: PendingSpendAction): boolean => {
      if (needed <= 0 || balance >= needed) {
        return true;
      }
      openCoinShop(needed, onReady);
      return false;
    },
    [openCoinShop],
  );

  const requireSpendCoins = useCallback(
    async (needed: number, onReady?: PendingSpendAction): Promise<boolean> => {
      if (needed <= 0) {
        return true;
      }
      if (!user) {
        openCoinShop(needed, onReady);
        return false;
      }
      try {
        const wallet = await api.getSpendWallet();
        setLastWalletTotal(wallet.total);
        return requireCoins(wallet.total, needed, onReady);
      } catch {
        openCoinShop(needed, onReady);
        return false;
      }
    },
    [api, openCoinShop, requireCoins, user],
  );

  useEffect(() => {
    return subscribeApiErrors((error) => {
      if (isInsufficientBalanceError(error)) {
        // Global catch has no resume context — shop only.
        openCoinShop();
      }
    });
  }, [openCoinShop]);

  useEffect(() => {
    if (!isOpen || !user) {
      return;
    }
    let cancelled = false;
    void api
      .getSpendWallet()
      .then((wallet) => {
        if (!cancelled) {
          setCoinBalance(wallet.total);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, isOpen, user]);

  const handlePurchased = useCallback((walletTotal: number): boolean => {
    setCoinBalance(walletTotal);
    setLastWalletTotal(walletTotal);

    const pending = pendingActionRef.current;
    const needed = neededCoinsRef.current;
    const covered = needed === null || walletTotal >= needed;
    if (!covered) {
      // Keep shop open so the fan can buy more; pending action stays armed.
      return false;
    }
    if (!pending) {
      return true;
    }

    pendingActionRef.current = null;
    neededCoinsRef.current = null;
    setIsOpen(false);
    setNeededCoins(null);

    void Promise.resolve()
      .then(() => pending())
      .catch(() => undefined);
    return true;
  }, []);

  const handleClose = useCallback(() => {
    closeCoinShop();
  }, [closeCoinShop]);

  const value = useMemo<CoinShopContextValue>(
    () => ({
      openCoinShop,
      closeCoinShop: handleClose,
      requireCoins,
      requireSpendCoins,
      lastWalletTotal,
    }),
    [handleClose, lastWalletTotal, openCoinShop, requireCoins, requireSpendCoins],
  );

  return (
    <CoinShopContext.Provider value={value}>
      {children}
      <CoinShopOverlay
        isOpen={isOpen}
        coinBalance={coinBalance}
        neededCoins={neededCoins}
        onClose={handleClose}
        onPurchased={handlePurchased}
      />
    </CoinShopContext.Provider>
  );
}

export function useCoinShop(): CoinShopContextValue {
  const context = useContext(CoinShopContext);
  if (!context) {
    throw new Error('useCoinShop must be used within CoinShopProvider');
  }
  return context;
}
